import React, { useState, useEffect } from 'react';
import StudentLayout from '../../components/StudentLayout';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Avatar,
  Divider,
  Chip,
  Alert,
  CircularProgress
} from '@mui/material';
import {
  Email as EmailIcon,
  Phone as PhoneIcon,
  Person as PersonIcon,
  CalendarToday as CalendarIcon,
  School as BatchIcon,
  Schedule as ScheduleIcon
} from '@mui/icons-material';
import axios from 'axios';

const StudentProfile = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [profile, setProfile] = useState(null);

  useEffect(() => {
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('access_token');
      const response = await axios.get('/api/student/profile', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProfile(response.data.student);
      setError('');
    } catch (err) {
      setError(err.response?.data?.message || 'Error loading profile');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <StudentLayout>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
          <CircularProgress />
        </Box>
      </StudentLayout>
    );
  }

  if (error || !profile) {
    return (
      <StudentLayout>
        <Alert severity="error">{error || 'Profile not found'}</Alert>
      </StudentLayout>
    );
  }

  return (
    <StudentLayout>
      <Box>
        <Typography variant="h4" fontWeight={700} gutterBottom>
          My Profile
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
          Your personal information and academy details
        </Typography>

        <Grid container spacing={3}>
          {/* Profile Card */}
          <Grid item xs={12} md={4}>
            <Card sx={{
              position: 'relative',
              overflow: 'hidden',
              background: 'linear-gradient(135deg, #FFFFFF 0%, #FFF9F0 100%)',
              border: '1px solid',
              borderColor: 'rgba(212, 175, 55, 0.2)',
              boxShadow: '0 2px 8px rgba(212, 175, 55, 0.15)',
              '&::before': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: 0,
                width: '4px',
                height: '100%',
                background: 'linear-gradient(180deg, #D4AF37 0%, #F4D03F 100%)',
              },
            }}>
              <CardContent sx={{ textAlign: 'center', pl: 3 }}>
                <Avatar
                  sx={{
                    width: 120,
                    height: 120,
                    margin: '0 auto',
                    background: 'linear-gradient(135deg, #D4AF37 0%, #F4D03F 100%)',
                    boxShadow: '0 2px 8px rgba(212, 175, 55, 0.3)',
                    fontSize: 48
                  }}
                >
                  {profile.student_name.charAt(0).toUpperCase()}
                </Avatar>
                <Typography variant="h5" fontWeight={600} sx={{ mt: 2, color: '#2C2416' }}>
                  {profile.student_name}
                </Typography>
                <Typography variant="body2" sx={{ color: '#8B7355' }}>
                  Student
                </Typography>
                <Chip
                  label={`Year ${profile.year_of_joining}`}
                  sx={{ mt: 2, bgcolor: 'rgba(212, 175, 55, 0.1)', color: '#8B4513', borderColor: 'rgba(212, 175, 55, 0.3)' }}
                  variant="outlined"
                />
              </CardContent>
            </Card>
          </Grid>

          {/* Details Card */}
          <Grid item xs={12} md={8}>
            <Card sx={{
              position: 'relative',
              overflow: 'hidden',
              background: 'linear-gradient(135deg, #FFFFFF 0%, #FFF9F0 100%)',
              border: '1px solid',
              borderColor: 'rgba(212, 175, 55, 0.2)',
              boxShadow: '0 2px 8px rgba(212, 175, 55, 0.15)',
              '&::before': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: 0,
                width: '4px',
                height: '100%',
                background: 'linear-gradient(180deg, #D4AF37 0%, #F4D03F 100%)',
              },
            }}>
              <CardContent sx={{ pl: 3 }}>
                <Typography variant="h6" fontWeight={600} sx={{ color: '#2C2416' }} gutterBottom>
                  Personal Information
                </Typography>
                <Divider sx={{ mb: 3, borderColor: 'rgba(212, 175, 55, 0.15)' }} />

                <Grid container spacing={3}>
                  <Grid item xs={12} sm={6}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                      <PersonIcon color="action" />
                      <Box>
                        <Typography variant="caption" color="text.secondary">
                          Student Name
                        </Typography>
                        <Typography variant="body1" fontWeight={500}>
                          {profile.student_name}
                        </Typography>
                      </Box>
                    </Box>
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                      <PersonIcon color="action" />
                      <Box>
                        <Typography variant="caption" color="text.secondary">
                          Parent Name
                        </Typography>
                        <Typography variant="body1" fontWeight={500}>
                          {profile.parent_name}
                        </Typography>
                      </Box>
                    </Box>
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                      <EmailIcon color="action" />
                      <Box>
                        <Typography variant="caption" color="text.secondary">
                          Email Address
                        </Typography>
                        <Typography variant="body1" fontWeight={500}>
                          {profile.email}
                        </Typography>
                      </Box>
                    </Box>
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                      <PhoneIcon color="action" />
                      <Box>
                        <Typography variant="caption" color="text.secondary">
                          Phone Number
                        </Typography>
                        <Typography variant="body1" fontWeight={500}>
                          {profile.phone}
                        </Typography>
                      </Box>
                    </Box>
                  </Grid>

                  {profile.alternate_phone && (
                    <Grid item xs={12} sm={6}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                        <PhoneIcon color="action" />
                        <Box>
                          <Typography variant="caption" color="text.secondary">
                            Alternate Phone
                          </Typography>
                          <Typography variant="body1" fontWeight={500}>
                            {profile.alternate_phone}
                          </Typography>
                        </Box>
                      </Box>
                    </Grid>
                  )}

                  <Grid item xs={12} sm={6}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                      <CalendarIcon color="action" />
                      <Box>
                        <Typography variant="caption" color="text.secondary">
                          Year of Joining
                        </Typography>
                        <Typography variant="body1" fontWeight={500}>
                          {profile.year_of_joining}
                        </Typography>
                      </Box>
                    </Box>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>

            {/* Batch Information */}
            {profile.batch && (
              <Card sx={{ 
                mt: 3,
                position: 'relative',
                overflow: 'hidden',
                background: 'linear-gradient(135deg, #FFFFFF 0%, #FFF9F0 100%)',
                border: '1px solid',
                borderColor: 'rgba(212, 175, 55, 0.2)',
                boxShadow: '0 2px 8px rgba(212, 175, 55, 0.15)',
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '4px',
                  height: '100%',
                  background: 'linear-gradient(180deg, #D4AF37 0%, #F4D03F 100%)',
                },
              }}>
                <CardContent sx={{ pl: 3 }}>
                  <Typography variant="h6" fontWeight={600} sx={{ color: '#2C2416' }} gutterBottom>
                    Batch Information
                  </Typography>
                  <Divider sx={{ mb: 3, borderColor: 'rgba(212, 175, 55, 0.15)' }} />

                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mb: 2 }}>
                    <BatchIcon color="primary" sx={{ fontSize: 40, mt: 0.5 }} />
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="h6" fontWeight={600}>
                        {profile.batch.name}
                      </Typography>
                      {profile.batch.description && (
                        <Typography variant="body2" color="text.secondary">
                          {profile.batch.description}
                        </Typography>
                      )}
                    </Box>
                  </Box>

                  {profile.batch.schedules && profile.batch.schedules.length > 0 && (
                    <Box sx={{ mt: 2, p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
                      <Typography variant="subtitle2" color="text.secondary" gutterBottom fontWeight={600}>
                        Class Schedule
                      </Typography>
                      <Box sx={{ mt: 1.5 }}>
                        {profile.batch.schedules.map((schedule, idx) => (
                          <Box 
                            key={idx} 
                            sx={{ 
                              display: 'flex', 
                              alignItems: 'center', 
                              gap: 1,
                              mb: idx < profile.batch.schedules.length - 1 ? 1 : 0
                            }}
                          >
                            <ScheduleIcon color="primary" sx={{ fontSize: 20 }} />
                            <Typography variant="body1">
                              <strong>{schedule.day_of_week}:</strong> {schedule.start_time} - {schedule.end_time}
                            </Typography>
                          </Box>
                        ))}
                      </Box>
                    </Box>
                  )}
                </CardContent>
              </Card>
            )}
          </Grid>
        </Grid>
      </Box>
    </StudentLayout>
  );
};

export default StudentProfile;
